/**
 * Whitelist test-send (#2575).
 *
 * The confirm dialog is the UI counterpart of
 * `MANUAL_CRON_HANDLERS.sideEffectWarning`: this is a real, unrecallable
 * mail delivery. History rows are marked `test_send`; the latch is not
 * touched. That does not make the mail itself a drill.
 */
import { useState } from 'react';
import type { CodexCapacityAlertMailKind } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Button, ConfirmDialog, FormField, RadioGroup, Textarea } from '../../ui/index.ts';
import { parseAccountIdList } from './codexCapacityStatus.ts';

interface Props {
  readonly sending: boolean;
  readonly onSend: (
    accountIds: readonly string[],
    kind: CodexCapacityAlertMailKind,
  ) => Promise<void>;
}

export function CodexCapacityTestSendPanel({ sending, onSend }: Props): JSX.Element {
  const t = useT();
  const [rawIds, setRawIds] = useState('');
  const [kind, setKind] = useState<CodexCapacityAlertMailKind>('elevated');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const accountIds = parseAccountIdList(rawIds);

  return (
    <div className="flex flex-col gap-4">
      <FormField
        label={t('codex_capacity.test_send.account_ids')}
        fieldId="codex-capacity-test-send-ids"
        hint={t('codex_capacity.test_send.account_ids_hint')}
      >
        <Textarea
          id="codex-capacity-test-send-ids"
          value={rawIds}
          onChange={(event) => {
            setRawIds(event.currentTarget.value);
            setLocalError(null);
          }}
          data-testid="codex-capacity-test-send-ids"
        />
      </FormField>

      <FormField label={t('codex_capacity.test_send.kind')} fieldId="codex-capacity-test-send-kind">
        <RadioGroup
          value={kind}
          onValueChange={(value) => {
            if (value === 'elevated' || value === 'recovered') setKind(value);
          }}
          ariaLabel={t('codex_capacity.test_send.kind')}
          testId="codex-capacity-test-send-kind"
          options={[
            { value: 'elevated', label: t('codex_capacity.test_send.kind_elevated') },
            { value: 'recovered', label: t('codex_capacity.test_send.kind_recovered') },
          ]}
        />
      </FormField>

      {localError !== null ? (
        <p className="m-0 text-dense text-bad" role="alert" data-testid="codex-capacity-test-send-error">
          {localError}
        </p>
      ) : null}

      <div>
        <Button
          variant="danger"
          onClick={() => {
            if (accountIds.length === 0) {
              setLocalError(t('codex_capacity.test_send.need_accounts'));
              return;
            }
            setSendError(null);
            setConfirmOpen(true);
          }}
          data-testid="codex-capacity-test-send-submit"
        >
          {t('codex_capacity.test_send.submit')}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        tone="danger"
        title={t('codex_capacity.test_send.confirm_title')}
        body={
          <div className="flex flex-col gap-3">
            <p
              className="m-0 rounded border border-bad-line bg-bad-dim/50 px-2.5 py-2 text-dense text-bad-bright"
              data-testid="codex-capacity-test-send-confirm-consequence"
            >
              {t('codex_capacity.test_send.confirm_consequence')}
            </p>
            <p className="m-0">{t('codex_capacity.test_send.confirm_detail')}</p>
            {sendError !== null ? (
              <p className="m-0 text-dense text-bad" data-testid="codex-capacity-test-send-confirm-error">
                {sendError}
              </p>
            ) : null}
          </div>
        }
        confirmLabel={t('codex_capacity.test_send.confirm_action')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={sending}
        onConfirm={() => {
          if (accountIds.length === 0) return;
          void onSend(accountIds, kind)
            .then(() => {
              setSendError(null);
              setConfirmOpen(false);
            })
            .catch((error: unknown) => {
              setSendError(error instanceof Error ? error.message : t('codex_capacity.error.generic'));
            });
        }}
        testId="codex-capacity-test-send-confirm"
      />
    </div>
  );
}
